import { Arrange, Divider, Spacer, Typography } from "@fullscript/aviary-web";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { Container } from "../Container";

import { FooterIcons } from "./FooterIcons";
import { FooterLinkList } from "./FooterLinkList";
import { getSubtleFootnoteProps } from "./getSubtleFootnoteProps";
import type { PageFooterProps } from "./PageFooter.types";

import * as styles from "./PageFooter.styles";

const PageFooter = ({
  type = "basic",
  isWholesale,
  customPrefooterPromo,
  customPrefooter,
  withSidebar,
  hideLinkList,
  hideShippingDisclaimer,
  hideHipaaLogo,
  isInverseTheme = false,
  customLegalDisclaimer,
}: PageFooterProps) => {
  const { t } = useTranslation();
  const { isUS } = useSharedGlobalConfig();
  const { desktop } = useBreakpoints();
  const isCenteredDisclaimerLayout =
    !!withSidebar && (type === "product" || type === "labs") && !isInverseTheme;
  const disclaimerCss = isCenteredDisclaimerLayout ? undefined : styles.fdaDisclaimer;

  const getCurrentYear = () => {
    return new Date().getFullYear();
  };

  const renderFullscriptCopyright = () => (
    <Typography {...getSubtleFootnoteProps(isInverseTheme)} as="span" isMarginless>
      &copy; {t(l.aviary.footer.FullscriptCopyRights, { currentYear: getCurrentYear() })}
    </Typography>
  );

  const renderLegalRow = (justify?: "center") => (
    <Arrange alignment="baseline" gap="small" justify={justify}>
      {renderFullscriptCopyright()}
      {renderLinkList()}
    </Arrange>
  );

  const renderFDAdisclaimer = () => {
    if (!isUS) return null;

    return (
      <div css={disclaimerCss}>
        <Typography {...getSubtleFootnoteProps(isInverseTheme)}>
          {t(l.aviary.footer.FDAdisclaimerLine1)}
        </Typography>
        {type !== "labs" && (
          <Typography {...getSubtleFootnoteProps(isInverseTheme)}>
            {t(l.aviary.footer.FDAdisclaimerLine2)}
          </Typography>
        )}
      </div>
    );
  };

  const renderShippingDisclaimer = () => {
    if (hideShippingDisclaimer) return null;

    return (
      <div css={styles.fdaDisclaimer}>
        <Typography {...getSubtleFootnoteProps(isInverseTheme)}>
          {t(l.aviary.footer.ShippingDisclaimer)}
        </Typography>
      </div>
    );
  };

  const renderLeftLayoutContent = () => {
    if (type === "checkout") {
      return renderShippingDisclaimer();
    }

    return (
      <>
        {renderFDAdisclaimer()}
        {!!customLegalDisclaimer && (
          <div css={disclaimerCss}>
            <Typography {...getSubtleFootnoteProps(isInverseTheme)}>
              {customLegalDisclaimer}
            </Typography>
          </div>
        )}
      </>
    );
  };

  const renderLinkList = () => {
    if (hideLinkList) return null;

    return <FooterLinkList type={type} isWholesale={isWholesale} isInverseTheme={isInverseTheme} />;
  };

  const inverseBottomBar = (
    <>
      <Divider
        customCss={styles.inverseBottomBarDivider}
        data-testid="page-footer-inverse-bottom-bar-divider"
      />
      <Arrange justify="spaceBetween" alignment="center" isWrapped={false} isFullWidth>
        {renderFullscriptCopyright()}
        {renderLinkList()}
      </Arrange>
    </>
  );

  const renderCenteredDisclaimerLayout = () => (
    <Arrange
      isVertical
      isFullWidth
      alignment="center"
      justify="center"
      customCss={styles.centeredLayout}
    >
      {renderLeftLayoutContent()}
      {desktop.lessThan ? (
        <>
          {renderLinkList()}
          {renderFullscriptCopyright()}
        </>
      ) : (
        renderLegalRow("center")
      )}
      <FooterIcons type={type} hideHipaaLogo={hideHipaaLogo} />
    </Arrange>
  );

  const renderMobileLayout = () => {
    if (type !== "basic") {
      if (isInverseTheme) {
        return (
          <>
            <Arrange isVertical isFullWidth justify="center" customCss={styles.centeredLayout}>
              <FooterIcons type={type} hideHipaaLogo={hideHipaaLogo} isInverseTheme />
              {renderLeftLayoutContent()}
            </Arrange>
            {inverseBottomBar}
          </>
        );
      }

      return (
        <Arrange isVertical isFullWidth justify="center" customCss={styles.centeredLayout}>
          <FooterIcons type={type} hideHipaaLogo={hideHipaaLogo} />
          {renderLeftLayoutContent()}
          {renderLinkList()}
          {renderFullscriptCopyright()}
        </Arrange>
      );
    }

    return (
      <Arrange isVertical alignment="center">
        {renderLinkList()}
        {renderFullscriptCopyright()}
      </Arrange>
    );
  };

  const renderDesktopLayout = () => {
    if (type !== "basic") {
      if (isInverseTheme) {
        return (
          <>
            <Arrange justify="spaceBetween" alignment="start" isWrapped={false}>
              <Arrange isVertical isFullWidth>
                {renderLeftLayoutContent()}
              </Arrange>
              <FooterIcons type={type} hideHipaaLogo={hideHipaaLogo} isInverseTheme />
            </Arrange>
            {inverseBottomBar}
          </>
        );
      }

      return (
        <Arrange justify="spaceBetween" alignment="center" isWrapped={false}>
          <Arrange isVertical isFullWidth>
            {renderLeftLayoutContent()}
            {renderLegalRow()}
          </Arrange>
          <div>
            <FooterIcons type={type} hideHipaaLogo={hideHipaaLogo} />
          </div>
        </Arrange>
      );
    }

    return (
      <Arrange justify="spaceBetween" alignment="center">
        {renderFullscriptCopyright()}
        {renderLinkList()}
      </Arrange>
    );
  };

  const renderPreFooter = () => {
    if (!customPrefooter) return null;

    return (
      <>
        {customPrefooter}
        <Spacer size="large" />
      </>
    );
  };

  const renderFooterBody = () => {
    if (isCenteredDisclaimerLayout) {
      return renderCenteredDisclaimerLayout();
    }

    return desktop.lessThan ? renderMobileLayout() : renderDesktopLayout();
  };

  const renderInverseFooterContent = () => (
    <>
      {!!customPrefooterPromo && (
        <div css={styles.promoFullBleedSection} data-testid="footer-app-promo-band">
          <Container withSidebar={withSidebar}>{customPrefooterPromo}</Container>
        </div>
      )}
      <div css={styles.inverseFullBleedSection} data-testid="page-footer-inverse-section">
        <Container withSidebar={withSidebar}>
          {customPrefooter}
          {renderFooterBody()}
        </Container>
      </div>
    </>
  );

  const renderFooterContent = () => {
    if (isInverseTheme) {
      return renderInverseFooterContent();
    }

    return (
      <Container withSidebar={withSidebar}>
        {renderPreFooter()}
        {renderFooterBody()}
      </Container>
    );
  };

  return (
    <footer css={styles.footerBase}>
      <Spacer size="xxlarge" />
      {renderFooterContent()}
      {!isInverseTheme && <Spacer size="xxlarge" />}
      {/* Height added in aviaryNormalize.styles.ts when StickyFooter is mounted */}
      <div data-footer="sticky" />
    </footer>
  );
};

export { PageFooter };
